/**
 * Decides whether the patient's own internet connection is weak, from samples
 * the probe (./probe.js) and the browser's Network Information API collect.
 * Pure — every rule here is unit-tested in ./__tests__/quality.test.js.
 *
 * Weak means either:
 *   - download speed under WEAK_DOWNLINK_MBPS, or
 *   - round-trip latency over WEAK_LATENCY_MS.
 * Both are judged on a median, never one sample, so a single hiccup cannot
 * raise the message; and clearing it needs a margin past the threshold, so a
 * connection sitting right on the line does not flicker it on and off.
 */

// What the exercise actually needs at once: the reference video (~1 Mbps for
// a typical mp4), the voice call (~50 kbps Opus) and the pose stream (tens of
// kbps). Under 1.5 Mbps something among them has to wait.
export const WEAK_DOWNLINK_MBPS = 1.5;
// Round trip to our frontend host on a warm connection. Everyday 4G/Wi-Fi sits
// at 50-200ms; past ~450ms the voice agent's replies and the live counter
// visibly lag behind the movement (ITU G.114 puts conversation at its limit
// around 400ms one-way including processing).
export const WEAK_LATENCY_MS = 450;
// Hysteresis: once weak, it takes clearly better numbers to call it good again.
export const RECOVERED_DOWNLINK_MBPS = 2;
export const RECOVERED_LATENCY_MS = 350;
// Latency is judged on the median of the last LATENCY_WINDOW samples (10s
// apart, so ~50s of history), once at least LATENCY_MIN_SAMPLES exist: one
// slow moment never raises the message, a sustained ~30s of it does.
export const LATENCY_WINDOW = 5;
export const LATENCY_MIN_SAMPLES = 3;
export const DOWNLINK_WINDOW = 2;

export const NETWORK_STATE = Object.freeze({
  checking: 'checking',
  // No internet at all: the browser says so, or our own host stopped answering.
  offline: 'offline',
  good: 'good',
  weak: 'weak',
});

// Consecutive pings that failed outright (no response at all) before the device
// counts as offline even though the browser still reports a network — Wi-Fi
// joined but with no internet behind it, a captive portal, a dead router.
export const UNREACHABLE_PINGS_FOR_OFFLINE = 2;

/**
 * Offline beats any quality verdict: speed and latency mean nothing without a
 * connection, and the patient needs to hear the plainer thing.
 */
export function resolveNetworkState({ isBrowserOffline, unreachablePings, quality }) {
  if (isBrowserOffline || unreachablePings >= UNREACHABLE_PINGS_FOR_OFFLINE) {
    return NETWORK_STATE.offline;
  }

  return quality;
}

export function median(values) {
  if (!values.length) {
    return null;
  }

  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/** Keeps the newest `size` samples. */
export function pushSample(samples, value, size) {
  return [...samples, value].slice(-size);
}

/**
 * @param {object} input
 * @param {number[]} input.latencySamples  recent round trips, ms
 * @param {number[]} input.downlinkSamples recent measured download speeds, Mbps
 * @param {number|null} input.browserDownlinkMbps navigator.connection.downlink, when the browser has it
 * @param {string} input.previous          the last NETWORK_STATE
 */
export function evaluateNetworkQuality({
  latencySamples = [],
  downlinkSamples = [],
  browserDownlinkMbps = null,
  previous = NETWORK_STATE.checking,
}) {
  const latency = latencySamples.length >= LATENCY_MIN_SAMPLES ? median(latencySamples) : null;
  const measuredDownlink =
    downlinkSamples.length >= DOWNLINK_WINDOW ? median(downlinkSamples) : null;
  // The slower of the two readings. Chromium's estimate comes from its own view
  // of past traffic and can lag behind a connection that just got worse (and
  // does not see DevTools throttling at all), so it is never allowed to overrule
  // a slow measurement — only to flag a slow link before we have measured one.
  const readings = [browserDownlinkMbps, measuredDownlink].filter(
    value => typeof value === 'number'
  );
  const downlink = readings.length ? Math.min(...readings) : null;

  if (latency === null && downlink === null) {
    return { state: previous, latencyMs: null, downlinkMbps: null };
  }

  const wasWeak = previous === NETWORK_STATE.weak;
  const latencyLimit = wasWeak ? RECOVERED_LATENCY_MS : WEAK_LATENCY_MS;
  const downlinkLimit = wasWeak ? RECOVERED_DOWNLINK_MBPS : WEAK_DOWNLINK_MBPS;
  const isLatencyWeak =
    latency !== null && (wasWeak ? latency >= latencyLimit : latency > latencyLimit);
  const isDownlinkWeak = downlink !== null && downlink < downlinkLimit;

  return {
    state: isLatencyWeak || isDownlinkWeak ? NETWORK_STATE.weak : NETWORK_STATE.good,
    latencyMs: latency,
    downlinkMbps: downlink,
  };
}
